import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, AlertTriangle } from "lucide-react";
import { PlatformNav } from "@/components/platform/AppShell";
import { NotificationBell } from "@/components/platform/NotificationBell";
import { TenantSwitcher } from "@/components/platform/TenantSwitcher";
import { SupportModeBar } from "@/components/platform/SupportMode";
import { COMPLIANCE_DEADLINE, daysUntil } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Header({ auditWarning = false }) {
  const days = daysUntil(COMPLIANCE_DEADLINE);
  const auth = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isLogin = loc.pathname === "/login";

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center gap-6">
        <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="header-logo-link">
          <div className="w-9 h-9 rounded-[8px] bg-[#003366] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="text-[15px] font-bold tracking-tight text-[#003366]">CX0 GRC Tracker</div>
            <div className="text-[11px] text-slate-500 font-mono">v0.1 · scaffold</div>
          </div>
        </Link>

        {auth?.isAuthed && !isLogin && <PlatformNav />}

        <div className="flex-1" />

        {!isLogin && (
          <div
            className="hidden 2xl:flex items-center gap-2 px-3 py-1.5 rounded-[8px] border border-slate-200 bg-slate-50"
            data-testid="deadline-countdown"
          >
            <span className="text-xs text-slate-500 uppercase tracking-wide">DPDP Deadline</span>
            <span className="font-mono text-[13px] font-semibold text-[#003366]">
              {days}d · {COMPLIANCE_DEADLINE}
            </span>
          </div>
        )}

        {auth?.isAuthed && !isLogin && (
          <>
            <TenantSwitcher />
            <NotificationBell />
            <div className="hidden xl:block text-sm text-slate-600" data-testid="header-username">
              <span className="font-medium text-[#003366]">{auth.user?.username}</span>
              <span className="ml-2 text-xs uppercase tracking-wide font-mono text-[#008080]" data-testid="header-role-label">
                {auth.user?.role_label || auth.user?.role}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { auth.logout(); nav("/login"); }}
              data-testid="header-logout-button"
              className="grc-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </>
        )}
      </div>

      {auditWarning && (
        <div
          className="w-full bg-amber-50 border-t border-amber-200"
          data-testid="audit-deadline-banner"
        >
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-2 flex items-center gap-2 text-[13px] text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            You are within 12 months of the DPDP compliance deadline. Prioritise CRITICAL findings.
          </div>
        </div>
      )}

      {auth?.isAuthed && !isLogin && auth.user?.is_demo_tenant && (
        <div className="w-full bg-amber-400 border-t border-amber-500" data-testid="demo-workspace-banner">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-1.5 text-center text-[12px] font-semibold text-amber-950">
            Demo workspace — sample data for demos and POCs, not a live customer organization
          </div>
        </div>
      )}
    </header>
    {auth?.isAuthed && !isLogin && <SupportModeBar />}
    </>
  );
}
